import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { brand } from "@/config/site";

const inputCls = "w-full border-b border-dh-cream/20 bg-transparent px-1 py-3 font-body text-base text-dh-cream placeholder:text-dh-muted/60 outline-none transition-colors duration-300 focus:border-dh-gold";
const labelCls = "mb-2 block font-body text-[11px] uppercase tracking-[0.2em] text-dh-muted";

const FIELDS = [
  ["recipient_name", "Recipient Full Name", true],
  ["line1", "Address Line 1", true],
  ["line2", "Address Line 2 (optional)", false],
  ["city", "City", true],
  ["state", "State / Province", true],
  ["postal_code", "Postal Code", true],
  ["country", "Country", true],
];

function Shell({ children }) {
  return (
    <section className="min-h-screen bg-dh-bg px-5 pt-16 pb-24 sm:pt-20">
      <div className="mx-auto max-w-[640px]">
        <div className="flex flex-col items-center text-center">
          <img src={brand.crest} alt="Dhamma Publishing House" className="h-16 w-auto" />
          <p className="mt-5 font-body text-[11px] uppercase tracking-ultra text-dh-gold">Dhamma Publishing House</p>
          <h1 className="mt-4 font-display text-3xl leading-[1.15] text-dh-cream sm:text-4xl">Print ARC Shipping Address</h1>
          <div className="gold-rule mx-auto mt-8 w-24" />
        </div>
        {children}
        <p className="mt-16 text-center font-body text-xs text-dh-muted">
          Questions? Contact{" "}
          <a href="mailto:Arcs@DhammaPublishingHouse.com" className="text-dh-gold underline-offset-4 hover:underline">Arcs@DhammaPublishingHouse.com</a>
        </p>
      </div>
    </section>
  );
}

export default function ArcShipping() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = "Print ARC Shipping | Dhamma Publishing House";
    let el = document.head.querySelector('meta[name="robots"]');
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", "robots");
      document.head.appendChild(el);
    }
    el.setAttribute("content", "noindex, nofollow");
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/arc/shipping/${token}`);
        setState({ loading: false, error: null, data: res.data });
      } catch (err) {
        const d = err?.response?.data?.detail;
        setState({ loading: false, error: typeof d === "string" ? d : "This shipping link is invalid, expired, or has already been replaced.", data: null });
      }
    })();
  }, [token]);

  const allRequired = ["recipient_name", "line1", "city", "state", "postal_code", "country"]
    .every((k) => (form[k] || "").trim().length > 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!allRequired || submitting) return;
    setSubmitting(true);
    try {
      await api.post(`/arc/shipping/${token}`, {
        recipient_name: form.recipient_name, line1: form.line1, line2: form.line2 || null,
        city: form.city, state: form.state, postal_code: form.postal_code, country: form.country,
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not save your address. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) {
    return <Shell><p className="mt-16 text-center font-body text-sm text-dh-muted">Loading…</p></Shell>;
  }
  if (state.error) {
    return (
      <Shell>
        <div data-testid="shipping-error" className="mt-14 border border-dh-gold/25 bg-dh-forest/15 p-8 text-center">
          <p className="font-body text-base leading-relaxed text-dh-cream/85">{state.error}</p>
        </div>
      </Shell>
    );
  }
  if (done || state.data.already_submitted) {
    return (
      <Shell>
        <div data-testid="shipping-received" className="mt-14 border border-dh-gold/25 bg-dh-forest/15 p-8 text-center sm:p-10">
          <p className="font-body text-[11px] uppercase tracking-ultra text-dh-gold">Address Received</p>
          <h2 className="mt-4 font-display text-3xl text-dh-cream">Thank you{state.data.recipient_name || form.recipient_name ? `, ${form.recipient_name || state.data.recipient_name}` : ""}.</h2>
          <p className="mx-auto mt-5 max-w-[480px] font-body text-base leading-relaxed text-dh-cream/80">
            Your shipping address has been received{state.data.assigned_copy_id ? ` for copy ${state.data.assigned_copy_id}` : ""}.
            Dhamma Publishing House will prepare your Print ARC shipment and follow up when it is on its way.
          </p>
          <p className="mt-6 font-body text-sm text-dh-muted">
            <Link to="/" className="text-dh-gold underline-offset-4 hover:underline">Return to Dhamma Publishing House</Link>
          </p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div data-testid="shipping-form" className="mt-12">
        <p className="text-center font-body text-sm leading-relaxed text-dh-cream/75">
          {state.data.reviewer_name}, your approved Print ARC{state.data.assigned_copy_id ? ` (${state.data.assigned_copy_id})` : ""} is being prepared.
          Please provide the shipping address for delivery. Your address is collected solely to fulfill
          your approved Print ARC shipment and is stored securely.
        </p>
        <form onSubmit={submit} className="mt-10 space-y-7">
          {FIELDS.map(([key, label, required]) => (
            <div key={key}>
              <label htmlFor={`ship_${key}`} className={labelCls}>{label}{required ? " *" : ""}</label>
              <input
                id={`ship_${key}`}
                data-testid={`ship-${key.replace(/_/g, "-")}`}
                value={form[key] || ""}
                onChange={(e) => setForm((s) => ({ ...s, [key]: e.target.value }))}
                className={inputCls}
                maxLength={200}
              />
            </div>
          ))}
          <button type="submit" data-testid="shipping-submit" disabled={!allRequired || submitting}
            className="mt-4 inline-flex w-full items-center justify-center gap-3 border border-dh-gold/70 bg-transparent px-10 py-4 font-body text-[11px] uppercase tracking-[0.22em] text-dh-gold transition-[background-color,color] duration-500 hover:bg-dh-gold hover:text-dh-bg2 disabled:cursor-not-allowed disabled:opacity-40">
            {submitting ? "Saving…" : "Submit Shipping Address"}
          </button>
        </form>
      </div>
    </Shell>
  );
}
